import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router';
import { Box, Grid, Typography, useTheme } from '@mui/material';
import { visuallyHidden } from '@mui/utils';
import { FORM, STRINGS, ROUTE_PATH } from '../../constants';
import useCustomSnackbar from '../../features/CustomSnackbar/useSnackbar';
import { useOverlay } from '../../features/Overlay';
import { CitysprintFlowStepper } from './components/CitysprintFlowStepper';
import { CitysprintParcelAddressFields } from './components/CitysprintParcelAddressFields';
import { CitysprintCollectionSchedulePicker } from './components/CitysprintCollectionSchedulePicker';
import { CitysprintQuoteSummarySection } from './components/CitysprintQuoteRequestPanel';
import { CitysprintParcelOrderSummary } from './components/CitysprintParcelOrderSummary';
import {
  BOOKING_TIMING_SECTION_REENABLE_MS,
  QUOTE_SESSION_LOCK_ERROR,
} from './constants';
import { hasCitysprintAddressCoreFields } from './helpers';
import {
  CitysprintWizardActions,
  CitysprintWizardSelectors,
  lockCitysprintQuoteSession,
} from './redux';
import { useEnsureCitysprintBasket } from './hooks/useEnsureCitysprintBasket';
import { useCitysprintAddressEditor } from './hooks/useCitysprintAddressEditor';
import { useCitysprintSchedule } from './hooks/useCitysprintSchedule';
import { useCitysprintQuoteSelection } from './hooks/useCitysprintQuoteSelection';
import { useCitysprintWizardExitGuard } from './hooks/useCitysprintWizardExitGuard';

const SendCitysprintParcel = () => {
  const theme = useTheme();
  const snackbar = useCustomSnackbar();
  const overlay = useOverlay();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();

  useEnsureCitysprintBasket();
  useCitysprintWizardExitGuard();

  const initialParcelStep = useSelector(
    CitysprintWizardSelectors.getParcelStepValues
  );
  const initialQuickQuotePrefill = useSelector(
    CitysprintWizardSelectors.getQuickQuoteValues
  );

  const initialRef = useRef({
    parcel: initialParcelStep,
    quickQuote: initialQuickQuotePrefill,
  });
  const initial = initialRef.current;

  const [bookingTimingSectionSuppressed, setBookingTimingSectionSuppressed] =
    useState(false);
  const [quoteSessionNextPending, setQuoteSessionNextPending] = useState(false);

  const addressChangeFanoutRef = useRef(null);
  const forwardAddressChanged = useCallback(
    meta => addressChangeFanoutRef.current?.(meta),
    []
  );

  const {
    delivery,
    collection,
    patchDelivery,
    patchCollection,
    applyAfdToDelivery,
    applyAfdToCollection,
    resetAddresses,
  } = useCitysprintAddressEditor({
    initial: initial.parcel,
    onAddressChanged: forwardAddressChanged,
  });

  const canProceed =
    hasCitysprintAddressCoreFields(delivery) &&
    hasCitysprintAddressCoreFields(collection);
  const collectionSectionEnabled = hasCitysprintAddressCoreFields(delivery);
  const bookingSectionEnabled = canProceed && !bookingTimingSectionSuppressed;

  const {
    collectionDate,
    timeHour,
    timeMinute,
    todayIso,
    collectionDateMaxIso,
    scheduleComplete,
    collectionScheduleInPast,
    setCollectionDate,
    setTimeHourMinute,
    reset: resetSchedule,
  } = useCitysprintSchedule({
    initial: initial.parcel,
    enabled: bookingSectionEnabled,
  });

  const handleQuickQuoteConsumed = useCallback(() => {
    dispatch(CitysprintWizardActions.clearQuickQuote());
  }, [dispatch]);

  const {
    selectedQuoteOption,
    selectedVehicleIndex,
    quoteValidityText,
    showVehicleQuoteSection,
    quotesFetchReady,
    quotesLoading,
    quoteLayout,
    selectedOptionKey,
    selectedVatAmount,
    handleGetQuote,
    handleSelectParcelVehicle,
    handleSelectQuoteOption,
    reset: resetQuoteSelection,
  } = useCitysprintQuoteSelection({
    quickQuotePrefill: initial.quickQuote,
    collectionDate,
    timeHour,
    timeMinute,
    collectionPostcode: collection.postcode,
    deliveryPostcode: delivery.postcode,
    bookingSectionEnabled,
    scheduleComplete,
    collectionScheduleInPast,
    snackbar,
    onQuickQuoteConsumed: handleQuickQuoteConsumed,
  });

  addressChangeFanoutRef.current = ({ immediate }) => {
    if (immediate) {
      setBookingTimingSectionSuppressed(true);
    }
    resetSchedule();
    resetQuoteSelection();
    dispatch(CitysprintWizardActions.resetParcelTimingAndSelection());
  };

  useEffect(() => {
    if (!location.state?.citysprintNavReset) {
      return;
    }
    resetAddresses();
    resetSchedule();
    resetQuoteSelection();
    setBookingTimingSectionSuppressed(false);
    navigate('.', { replace: true, state: null });
  }, [
    location.state?.citysprintNavReset,
    navigate,
    resetAddresses,
    resetQuoteSelection,
    resetSchedule,
  ]);

  useEffect(() => {
    if (!bookingTimingSectionSuppressed) {
      return undefined;
    }
    const id = setTimeout(() => {
      setBookingTimingSectionSuppressed(false);
    }, BOOKING_TIMING_SECTION_REENABLE_MS);
    return () => clearTimeout(id);
  }, [delivery.postcode, collection.postcode, bookingTimingSectionSuppressed]);

  useEffect(() => {
    if (quotesLoading) {
      overlay.show();
    } else {
      overlay.hide();
    }
    return () => {
      overlay.hide();
    };
  }, [quotesLoading, overlay.show, overlay.hide]);

  const buildParcelStepPayload = useCallback(
    () => ({
      delivery: { ...delivery },
      collection: { ...collection },
      collectionDate,
      timeHour,
      timeMinute,
      selectedQuote: selectedQuoteOption,
    }),
    [
      delivery,
      collection,
      collectionDate,
      timeHour,
      timeMinute,
      selectedQuoteOption,
    ]
  );

  const goCompleteBooking = useCallback(async () => {
    if (!selectedQuoteOption) {
      return;
    }
    const parcelStepPayload = buildParcelStepPayload();
    try {
      await dispatch(lockCitysprintQuoteSession(parcelStepPayload)).unwrap();
    } catch {
      snackbar.showError({ message: QUOTE_SESSION_LOCK_ERROR });
      return;
    }
    dispatch(CitysprintWizardActions.patchParcelStep(parcelStepPayload));
    navigate(ROUTE_PATH.SEND_A_CITYSPRINT_PARCEL_COMPLETE_BOOKING);
  }, [
    selectedQuoteOption,
    buildParcelStepPayload,
    dispatch,
    navigate,
    snackbar,
  ]);

  const handleNextStep = async () => {
    if (!selectedQuoteOption || quoteSessionNextPending) {
      return;
    }
    setQuoteSessionNextPending(true);
    try {
      await goCompleteBooking();
    } finally {
      setQuoteSessionNextPending(false);
    }
  };

  const canGoNext = Boolean(showVehicleQuoteSection && selectedQuoteOption);

  const persistParcelStepForStepper = useCallback(() => {
    dispatch(CitysprintWizardActions.patchParcelStep(buildParcelStepPayload()));
  }, [dispatch, buildParcelStepPayload]);

  return (
    <Box
      sx={{
        py: { xs: 2 },
        p: { md: 4 },
        background: theme.vars.palette.primary.pageBackground,
        flexGrow: 1,
      }}
    >
      <Typography variant='h1' sx={visuallyHidden}>
        {STRINGS.PARCEL_DETAILS}
      </Typography>
      <Grid container spacing={0} alignItems='center' justifyContent='center'>
        <Grid size={{ xs: 12, md: 10, lg: 8, xl: 6 }}>
          <CitysprintFlowStepper
            activeStep={0}
            maxReachableStep={canGoNext ? 1 : 0}
            onBeforeNavigate={persistParcelStepForStepper}
          />

          <form
            id='sendCitysprintParcelForm'
            onSubmit={e => {
              e.preventDefault();
            }}
          >
            <Grid container spacing={2} sx={{ mt: 2 }}>
              <Grid size={{ xs: 12, md: 8 }}>
                <CitysprintParcelAddressFields
                  title={STRINGS.WHERE_YOUR_PARCEL_GOES}
                  titleId='citysprint-destination-title'
                  idPrefix='delivery'
                  lookupInputId='citysprint-delivery-address'
                  labels={{
                    postcode: FORM.ADDRESS_BOOK_FIELDS.POSTCODE.LABEL,
                    organisation:
                      FORM.SHIPMENT_FIELDS.DESTINATION_ORGANISATION.LABEL,
                    street: FORM.SHIPMENT_FIELDS.DESTINATION_STREET.LABEL,
                    locality: FORM.SHIPMENT_FIELDS.DESTINATION_LOCALITY.LABEL,
                    town: FORM.SHIPMENT_FIELDS.DESTINATION_CITY.LABEL,
                    county: FORM.SHIPMENT_FIELDS.DESTINATION_COUNTY.LABEL,
                  }}
                  address={delivery}
                  onPatch={patchDelivery}
                  onAfd={applyAfdToDelivery}
                />

                <CitysprintParcelAddressFields
                  title={STRINGS.WHERE_YOUR_PARCEL_FROM}
                  titleId='citysprint-collection-title'
                  idPrefix='collection'
                  lookupInputId='citysprint-collection-address'
                  labels={{
                    postcode: FORM.ADDRESS_BOOK_FIELDS.POSTCODE.LABEL,
                    organisation:
                      FORM.SHIPMENT_FIELDS.COLLECTION_ORGANISATION.LABEL,
                    street: FORM.SHIPMENT_FIELDS.COLLECTION_STREET.LABEL,
                    locality: FORM.SHIPMENT_FIELDS.COLLECTION_LOCALITY.LABEL,
                    town: FORM.SHIPMENT_FIELDS.COLLECTION_CITY.LABEL,
                    county: FORM.SHIPMENT_FIELDS.COLLECTION_COUNTY.LABEL,
                  }}
                  address={collection}
                  onPatch={patchCollection}
                  onAfd={applyAfdToCollection}
                  disabled={!collectionSectionEnabled}
                />

                <CitysprintCollectionSchedulePicker
                  enabled={bookingSectionEnabled}
                  collectionDate={collectionDate}
                  timeHour={timeHour}
                  timeMinute={timeMinute}
                  todayIso={todayIso}
                  collectionDateMaxIso={collectionDateMaxIso}
                  onChangeDate={setCollectionDate}
                  onChangeTime={setTimeHourMinute}
                  collectionScheduleInPast={collectionScheduleInPast}
                  quoteValidityText={quoteValidityText}
                  onGetQuote={() => void handleGetQuote()}
                  getQuoteDisabled={!quotesFetchReady || quotesLoading}
                />

                {showVehicleQuoteSection && (
                  <CitysprintQuoteSummarySection
                    quoteLayout={quoteLayout}
                    selectedVehicleIndex={selectedVehicleIndex}
                    onSelectVehicle={handleSelectParcelVehicle}
                    hideServiceCards={quotesLoading}
                    onSelectOption={handleSelectQuoteOption}
                    selectedOptionKey={selectedOptionKey}
                  />
                )}
              </Grid>

              <Grid size={{ xs: 12, md: 4 }}>
                <CitysprintParcelOrderSummary
                  selectedQuoteOption={selectedQuoteOption}
                  selectedVatAmount={selectedVatAmount}
                  canGoNext={canGoNext}
                  nextStepPending={quoteSessionNextPending}
                  onNextStep={handleNextStep}
                />
              </Grid>
            </Grid>
          </form>
        </Grid>
      </Grid>
    </Box>
  );
};

export default SendCitysprintParcel;
